import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoadWithInput } from "@redotech/react-util/load";
import { PageContext } from "@redotech/redo-agents-common/page-context";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { LengthUnit } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import type { Parcel } from "@redotech/redo-fulfillment-parcel-common/parcel";
import {
  CarrierParcel,
  CustomParcelType,
  ParcelType,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import { t } from "@redotech/redo-i18n";
import DotsReorderIcon from "@redotech/redo-icons/dots-reorder.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import Plus from "@redotech/redo-icons/plus.svg";
import { useAskRedoPage } from "@redotech/redo-merchant-app-common/ask-redo/use-ask-redo-page";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoCommandMenuItem } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { LabelText } from "@redotech/redo-web/arbiter-components/common/descriptors";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  CommandMenuProvider,
  RedoTableCommandMenuCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import {
  Sortable,
  SortableContent,
  SortableItem,
  SortableItemHandle,
} from "@redotech/redo-web/components/ui/sortable";
import { ExpandableDropdown } from "@redotech/redo-web/expandable-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import {
  LogoSize,
  ShippingLogo,
} from "@redotech/redo-web/fulfillment/common/shipping-logo";
import Trash from "@redotech/redo-web/icon-old/trash2.svg";
import { SkeletonText } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { nonEmptyValidator } from "@redotech/ui/validators";
import {
  arrayEqual,
  nullishEqual,
  numberEqual,
  optionalEqual,
  stringEqual,
} from "@redotech/util/equal";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import {
  ParcelContext,
  ReloadParcelsContext,
} from "../services/parcel-service";
import { adoptPersistedParcelIds, parcelRowId } from "./parcel-rows";

const PARCELS_PAGE_CONTEXT: PageContext = {
  pageTitle: "Packages",
  pageDescription:
    "The merchant is on the OMS Packages settings page, where they can create, edit, and manage shipping parcels and carrier packages.",
  preferredAgent: "oms",
  preferredSkill: "oms",
};

const PARCEL_CHANGES = ["upsert-parcel"];

enum Carrier {
  USPS = "usps",
  UPS = "ups",
  FEDEX = "fedex",
  DHL = "dhlexpress",
}

const convertCarrierParcelToCarrier = (
  carrierParcel: string | null | undefined,
): Carriers | null => {
  if (!carrierParcel) {
    return null;
  }
  if (carrierParcel.includes("usps")) {
    return Carriers.USPS;
  }
  if (carrierParcel.includes("ups")) {
    return Carriers.UPS;
  }
  if (carrierParcel.includes("fedex")) {
    return Carriers.FEDEX;
  }
  if (carrierParcel.includes("dhlexpress")) {
    return Carriers.DHL_ECS;
  }
  return null;
};

function itemToTextOption<T extends string | number | symbol>(
  item: T,
  displayNameFn: (item: T) => string = (item) => item.toString(),
): RedoListItem<T> {
  return { id: item, type: "text", value: item, text: displayNameFn(item) };
}

function mapTextOptions<T extends string | number | symbol>(
  list: T[],
  displayNameFn?: (item: T) => string,
): RedoListItem<T>[] {
  return list.map((item) => itemToTextOption(item, displayNameFn));
}

const getCarrierDisplayName = (carrier: Carrier): string => {
  switch (carrier) {
    case Carrier.USPS:
      return "USPS";
    case Carrier.UPS:
      return "UPS";
    case Carrier.FEDEX:
      return "FedEx";
    case Carrier.DHL:
      return t("{{carrier}} Express", { carrier: "DHL" });
  }
};

export const parcelDefault = (): ParcelValue => ({
  id: undefined,
  name: "",
  type: CustomParcelType.BOX,
  length: 0,
  width: 0,
  height: 0,
  lengthUnit: "in",
  weight: 0,
  weightUnit: "oz",
  isDefault: false,
  barcodes: [],
  carrierParcel: undefined,
});

// Formatting "CamelCase" into "Camel Case"
export const formatParcelName = (input: string) => {
  return (
    input
      // insert a space between letter and number
      .replace(/([A-z])([0-9])/g, "$1 $2")

      // insert a space between lower & upper
      .replace(/([a-z])([A-Z])/g, "$1 $2")

      // space before last upper in a sequence followed by lower
      .replace(/\b([A-Z]+)([A-Z])([a-z])/, "$1 $2$3")

      // uppercase the first character
      .replace(/^./, function (str) {
        return str.toUpperCase();
      })

      // format FedEx correctly
      .replace("Fed Ex", "FedEx")
  );
};

export const formatDimensionString = (dimensionString: string) => {
  return dimensionString.includes("in")
    ? `${dimensionString.replaceAll("in", "")} in`
    : dimensionString;
};

export const getDimensionString = (parcel: CarrierParcel) => {
  return (parcel.dimensions || []).slice(1).reduce(
    (acc, dimension) => {
      return acc + `, ${formatDimensionString(dimension)}`;
    },
    formatDimensionString(parcel.dimensions?.[0] || ""),
  );
};

enum ParcelTab {
  CUSTOM_PARCEL = "custom-parcel",
  CARRIER_PARCEL = "carrier-parcel",
}

const parcelTabFriendlyName: Record<ParcelTab, string> = {
  [ParcelTab.CUSTOM_PARCEL]: "Custom package",
  [ParcelTab.CARRIER_PARCEL]: "Carrier package",
};

const parcelTabs = [ParcelTab.CUSTOM_PARCEL, ParcelTab.CARRIER_PARCEL];

export const parcelInput = groupInput({
  id: input<string | undefined>({}),
  name: input<string>({ validator: nonEmptyValidator }),
  type: input<string>({ validator: nonEmptyValidator }),
  length: input<number>({}),
  width: input<number>({}),
  height: input<number>({}),
  lengthUnit: input<string>({ validator: nonEmptyValidator }),
  weight: input<number>({}),
  weightUnit: input<string>({ validator: nonEmptyValidator }),
  isDefault: input<boolean>(),
  barcodes: input<string[]>({ equal: arrayEqual(stringEqual) }),
  carrierParcel: input<CarrierParcel | undefined>({
    equal: optionalEqual<CarrierParcel>(
      (a, b) =>
        stringEqual(a.parcelType, b.parcelType) &&
        stringEqual(a.carrier, b.carrier) &&
        stringEqual(a.name, b.name) &&
        arrayEqual(stringEqual)(a.dimensions, b.dimensions) &&
        nullishEqual(stringEqual)(a.description, b.description) &&
        nullishEqual(stringEqual)(a.human_readable, b.human_readable) &&
        nullishEqual(numberEqual)(a.max_weight, b.max_weight),
    ),
  }),
});

export type ParcelValue = InputSchema.Value<typeof parcelInput>;

export type ParcelForm = InputSchema.Input<typeof parcelInput>;

const firstParcelDefault = (): ParcelValue => ({
  id: undefined,
  name: "",
  type: CustomParcelType.BOX,
  length: 0,
  width: 0,
  height: 0,
  lengthUnit: "in",
  weight: 0,
  weightUnit: "oz",
  isDefault: true,
  barcodes: [],
  carrierParcel: undefined,
});

export const parcelToParcelValue = (parcel: Parcel): ParcelValue => {
  if (!("carrier" in parcel)) {
    return {
      id: parcel._id?.toString(),
      type: parcel.type,
      length: parcel.length,
      width: parcel.width,
      height: parcel.height,
      lengthUnit: parcel.lengthUnit,
      weight: parcel.weight,
      weightUnit: parcel.weightUnit,
      name: parcel.name,
      isDefault: !!parcel.default,
      barcodes: parcel.barcodes ?? [],
      carrierParcel: undefined,
    };
  } else {
    return {
      id: parcel._id?.toString(),
      name: parcel.name,
      type: CustomParcelType.BOX,
      length: 0,
      width: 0,
      height: 0,
      lengthUnit: LengthUnit.INCH,
      weight: 0,
      weightUnit: WeightUnit.Oz,
      isDefault: !!parcel.default,
      barcodes: parcel.barcodes ?? [],
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      carrierParcel: {
        carrier: parcel.carrier,
        description: parcel.description || "",
        dimensions: parcel.dimensions || [],
        human_readable: parcel.human_readable || "",
        max_weight: parcel.max_weight,
        name: parcel.name,
      } as CarrierParcel,
    };
  }
};

export const parcelValueToParcel = (
  value: ParcelValue,
  position?: number,
): Parcel => {
  if (value.carrierParcel) {
    return {
      default: value.isDefault,
      parcelType: ParcelType.CARRIER,
      carrier: value.carrierParcel.carrier,
      description: value.carrierParcel.description,
      dimensions: value.carrierParcel.dimensions,
      human_readable: value.carrierParcel.human_readable,
      max_weight: value.carrierParcel.max_weight,
      name: value.carrierParcel.name,
      barcodes: value.barcodes,
      position,
    };
  } else {
    return {
      default: value.isDefault,
      parcelType: ParcelType.CUSTOM,
      name: value.name,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      type: value.type as CustomParcelType,
      length: +value.length,
      width: +value.width,
      height: +value.height,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      lengthUnit: value.lengthUnit as LengthUnit,
      weight: +value.weight,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      weightUnit: value.weightUnit as WeightUnit,
      barcodes: value.barcodes,
      position,
    };
  }
};

export const ParcelModal = memo(function ParcelModal({
  open,
  onSave,
  setOpen,
  input,
  isEditing = false,
  customOnly = false,
  showSingleUse = false,
  addToSaved = true,
  setAddToSaved,
  numParcels,
  carrierParcels,
  existingParcels = [],
}: {
  open: boolean;
  /** Awaited before the modal closes, so the button can show progress. */
  onSave: (parcels: ParcelValue[]) => void | Promise<void>;
  setOpen: (open: boolean) => void;
  input: ParcelForm;
  isEditing?: boolean;
  /** Skip the carrier tab entirely; callers that only want a custom package. */
  customOnly?: boolean;
  showSingleUse?: boolean;
  addToSaved?: boolean;
  setAddToSaved?: (addToSaved: boolean) => void;
  numParcels?: number;
  carrierParcels?: CarrierParcel[];
  existingParcels?: ParcelValue[];
}) {
  const [selectedCarrierParcels, setSelectedCarrierParcels] = useState<
    CarrierParcel[]
  >([]);
  const [saving, setSaving] = useState(false);
  // Track which carrier dropdown is currently open
  const [openCarrierIndex, setOpenCarrierIndex] = useState<number | undefined>(
    undefined,
  );

  // Get names of existing carrier parcels (not custom parcels)
  const existingCarrierParcelNames = existingParcels
    .filter((p) => p.carrierParcel)
    .map((p) => p.name);

  const getCarrierOptions = (carrier: Carrier): RedoListItem<CarrierParcel>[] =>
    carrierParcels
      ?.filter(
        (parcel) =>
          parcel.carrier === carrier &&
          !existingCarrierParcelNames.includes(parcel.name),
      )
      .map((parcel) => ({
        type: "text",
        leadingItem: {
          type: "icon",
          Icon: () => <Flex style={{ width: "16px", maxWidth: "16px" }} />,
        },
        id: parcel.name,
        value: parcel,
        text: formatParcelName(parcel.name),
      })) || [];

  const getCarrierHeader = (carrier: Carrier) => {
    const enumCarrier = convertCarrierParcelToCarrier(carrier);
    return (
      <Flex>
        <ShippingLogo carrier={enumCarrier} size={LogoSize.MEDIUM} />
        <div>{getCarrierDisplayName(carrier)} Package</div>
      </Flex>
    );
  };

  const handleCarrierParcelChange = useHandler((newValue: CarrierParcel[]) => {
    setSelectedCarrierParcels(newValue);
  });

  const handleCarrierDropdownOpen = useHandler((index: number) => {
    setOpenCarrierIndex(index);
  });

  const carriers = [Carrier.USPS, Carrier.UPS, Carrier.FEDEX, Carrier.DHL];

  const [currentTab, setCurrentTab] = useState<ParcelTab>(
    ParcelTab.CUSTOM_PARCEL,
  );
  const [barcodeInputValue, setBarcodeInputValue] = useState("");
  const {
    name,
    type,
    length,
    width,
    height,
    lengthUnit,
    weight,
    weightUnit,
    isDefault,
    barcodes,
  } = input.inputs;
  const allErrors = input.allErrors;

  useEffect(() => {
    if (!addToSaved) {
      name.setValue("Single use package");
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToSaved]);

  const closeModal = () => {
    setCurrentTab(ParcelTab.CUSTOM_PARCEL);
    setSelectedCarrierParcels([]);
    setOpenCarrierIndex(undefined);
    setOpen(false);
  };

  const canSubmit =
    (currentTab === ParcelTab.CUSTOM_PARCEL && !allErrors.length) ||
    (currentTab === ParcelTab.CARRIER_PARCEL &&
      selectedCarrierParcels.length > 0);

  function renderTab(tab: ParcelTab) {
    switch (tab) {
      case ParcelTab.CUSTOM_PARCEL: {
        return (
          <>
            {addToSaved && (
              <RedoTextInput
                label="Parcel name"
                placeholder="Add package name"
                setValue={name.setValue}
                value={name.value}
              />
            )}
            <RedoRadioGroup
              gap="md"
              label="Package type"
              options={[
                { id: "box", label: "Box" },
                { id: "envelope", label: "Envelope" },
                { id: "soft_pack", label: "Soft package or satchel" },
              ]}
              setValue={type.setValue}
              size="xs"
              value={type.value}
            />
            <Flex dir="column" style={{ width: "100%" }}>
              <LabelText label="Size" size="xs" />
              <Flex>
                <RedoIncrementDecrement
                  dataTestId="parcel-length"
                  description="Length"
                  fullWidth
                  min={0}
                  setValue={length.setValue}
                  size="small"
                  value={length.value}
                />
                <RedoIncrementDecrement
                  dataTestId="parcel-width"
                  description="Width"
                  fullWidth
                  min={0}
                  setValue={width.setValue}
                  size="small"
                  value={width.value}
                />
                <RedoIncrementDecrement
                  dataTestId="parcel-height"
                  description="Height"
                  fullWidth
                  min={0}
                  setValue={height.setValue}
                  size="small"
                  value={height.value}
                />
                <RedoSingleSelectDropdownInput
                  fullWidth
                  options={mapTextOptions(["in", "cm"])}
                  optionSelected={({ item }) => lengthUnit.setValue(item.value)}
                  selectedItem={itemToTextOption(lengthUnit.value)}
                />
              </Flex>
            </Flex>
            <Flex dir="column" style={{ width: "100%" }}>
              <LabelText label="Weight when empty" size="xs" />
              <Flex style={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                <RedoIncrementDecrement
                  dataTestId="parcel-weight"
                  fullWidth
                  min={0}
                  setValue={weight.setValue}
                  size="small"
                  value={weight.value}
                />
                <RedoSingleSelectDropdownInput
                  fullWidth
                  options={mapTextOptions(["oz", "lb", "g", "kg"])}
                  optionSelected={({ item }) => weightUnit.setValue(item.value)}
                  selectedItem={itemToTextOption(weightUnit.value)}
                />
              </Flex>
            </Flex>
            {addToSaved && (
              <RedoTagInputField
                delimiters={["newline", "comma"]}
                description="Scan any of these on the packing station to select this parcel. Use the barcode already printed on your boxes."
                inputValue={barcodeInputValue}
                label="Custom barcodes"
                placeholder="Add a barcode (hit enter)"
                setInputValue={setBarcodeInputValue}
                setTags={barcodes.setValue}
                tags={barcodes.value.map((barcode) => ({ text: barcode }))}
              />
            )}
            {showSingleUse && setAddToSaved && (
              <RedoCheckbox
                description="This will be added to your list of saved parcels for future use"
                label="Add to saved parcels"
                setValue={setAddToSaved!} // eslint-disable-line @typescript-eslint/no-non-null-assertion
                size="xs"
                value={addToSaved}
              />
            )}
            {!!numParcels && addToSaved && (
              <RedoCheckbox
                description="Used to calculate rates at checkout and pre-selected when buying labels"
                label="Use as default package"
                setValue={isDefault.setValue}
                size="xs"
                value={isDefault.value}
              />
            )}
          </>
        );
      }
      case ParcelTab.CARRIER_PARCEL: {
        return (
          <>
            <Flex dir="column">
              {carriers.map((carrier, index) => (
                <ExpandableDropdown
                  header={getCarrierHeader(carrier)}
                  id={index.toString()}
                  items={getCarrierOptions(carrier)}
                  key={carrier}
                  onOpen={handleCarrierDropdownOpen}
                  overrideOpen={openCarrierIndex === index}
                  value={selectedCarrierParcels}
                  valueChange={handleCarrierParcelChange}
                />
              ))}
            </Flex>
            {!carrierParcels && <>Loading...</>}
          </>
        );
      }
    }
  }

  return (
    <RedoModal
      buttonPlacement="full"
      footerBorder
      isOpen={open}
      modalSize="sm"
      noPadding
      onModalCloseRequested={closeModal}
      primaryButton={{
        text: "Confirm",
        size: "md",
        onClick: async () => {
          let saveValues: ParcelValue[];
          if (currentTab === ParcelTab.CUSTOM_PARCEL) {
            saveValues = [input.value];
          } else {
            // Add all selected carrier parcels
            saveValues = selectedCarrierParcels.map(
              (selectedParcel, index) => ({
                id: undefined,
                name: selectedParcel.name,
                type: CustomParcelType.BOX,
                length: 0,
                width: 0,
                height: 0,
                lengthUnit: "in",
                weight: 0,
                weightUnit: "oz",
                // Only first one can be default
                isDefault: index === 0 && (isDefault.value ?? false),
                barcodes: [],
                carrierParcel: selectedParcel,
              }),
            );
          }
          setSaving(true);
          try {
            await onSave(saveValues);
          } catch {
            // Leave the modal open so the merchant can retry; the caller
            // surfaces the failure.
            return;
          } finally {
            setSaving(false);
          }
          // Clear state after save
          setSelectedCarrierParcels([]);
          setOpenCarrierIndex(undefined);
          setCurrentTab(ParcelTab.CUSTOM_PARCEL);
          setOpen(false);
        },
        disabled: !canSubmit || saving,
        pending: saving,
      }}
      secondaryButton={{ size: "md", text: "Cancel", onClick: closeModal }}
      title={isEditing ? "Edit package" : "Add package"}
    >
      {/* Hide tabs when editing or custom-only - show just the custom form */}
      {!isEditing && !customOnly && (
        <RedoHorizontalTabs
          pl="2xl"
          selectedTab={currentTab}
          setSelectedTab={setCurrentTab}
          tabs={parcelTabs.map((key) => ({
            key,
            label: parcelTabFriendlyName[key],
          }))}
        />
      )}
      <Flex dir="column" gap="xl" pb="none" pt="2xl" px="2xl" w="full">
        {/* When editing, always render custom parcel form regardless of tab */}
        {renderTab(
          isEditing || customOnly ? ParcelTab.CUSTOM_PARCEL : currentTab,
        )}
      </Flex>
    </RedoModal>
  );
});

interface ParcelAutomationReference {
  flowId: string;
  flowName: string;
  enabled: boolean;
  stepId: string;
  stepName?: string;
}

/** Only rendered when `references` is non-empty; a clean delete is not gated. */
const DeleteParcelModal = memo(function DeleteParcelModal({
  parcelName,
  references,
  onCancel,
  onConfirm,
}: {
  parcelName: string;
  references: ParcelAutomationReference[];
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const team = useRequiredContext(TeamContext);
  const referenceCount = references.length;

  // One row per automation. References arrive in flow order, so the first one
  // seen is the step the editor should open on.
  const affectedFlows = useMemo(() => {
    const byFlow = new Map<
      string,
      {
        flowId: string;
        flowName: string;
        enabled: boolean;
        firstStepId: string;
        count: number;
      }
    >();
    for (const reference of references) {
      const existing = byFlow.get(reference.flowId);
      if (existing) {
        existing.count += 1;
      } else {
        byFlow.set(reference.flowId, {
          flowId: reference.flowId,
          flowName: reference.flowName,
          enabled: reference.enabled,
          firstStepId: reference.stepId,
          count: 1,
        });
      }
    }
    return [...byFlow.values()];
  }, [references]);

  return (
    <RedoModal
      onModalCloseRequested={onCancel}
      primaryButton={{
        text: "Delete anyway",
        onClick: onConfirm,
        theme: "destructive",
        size: "md",
      }}
      secondaryButton={{ size: "md", text: "Cancel", onClick: onCancel }}
      subtitle={`"${parcelName}" will be removed from your packages.`}
      theme="warn"
      title="Delete package?"
    >
      <Flex dir="column" gap="lg" pb="none" pt="xl" px="2xl" w="full">
        <RedoAlert
          color="warning"
          description="Deleting it leaves those steps referencing a package that no longer exists. Choose a different package for them first."
          fullWidth
          size="sm"
          title={
            referenceCount === 1
              ? "1 automation step references this package"
              : `${referenceCount} automation steps reference this package`
          }
        />
        <Flex dir="column" gap="sm">
          {affectedFlows.map((flow) => (
            <Flex align="center" gap="sm" key={flow.flowId}>
              <RedoExternalLink
                fontSize="sm"
                fontWeight="medium"
                text={flow.flowName}
                url={`/stores/${team._id}/settings/oms/automations/${flow.flowId}?selected=${flow.firstStepId}`}
              />
              <Text fontSize="sm" textColor="secondary">
                {flow.count === 1
                  ? "(1 reference)"
                  : `(${flow.count} references)`}
                {flow.enabled ? "" : " · disabled"}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </RedoModal>
  );
});

// Sortable row component for the parcels table
const SortableParcelRow = memo(function SortableParcelRow({
  parcel,
  index,
  onEdit,
  onDelete,
  onSetDefault,
  loading,
}: {
  parcel: ParcelValue;
  index: number;
  onEdit: (index: number) => void;
  onDelete: (parcel: ParcelValue) => void;
  onSetDefault: (parcel: ParcelValue) => void;
  loading?: boolean;
}) {
  const menuItems: RedoCommandMenuItem[] = [
    { Icon: Trash, text: "Delete package", onClick: () => onDelete(parcel) },
  ];

  if (!parcel.carrierParcel) {
    menuItems.unshift({
      text: "Edit package...",
      onClick: () => onEdit(index),
    });
  }

  if (!parcel.isDefault) {
    menuItems.unshift({
      text: "Make default package",
      onClick: () => onSetDefault(parcel),
    });
  }

  return (
    <SortableItem
      style={{
        display: "grid",
        gridTemplateColumns: "3fr 2fr 1fr 80px 40px",
        height: "48px",
        borderBottom: "1px solid var(--redo-colors-border-border-secondary)",
      }}
      value={parcelRowId(parcel)}
    >
      <Flex
        align="center"
        overflow="hidden"
        style={{ maxHeight: "48px", minWidth: 0 }}
      >
        {loading ? (
          <SkeletonText length={10} />
        ) : (
          <RedoTableTextCell
            subtext={parcel.isDefault ? "Default" : undefined}
            text={formatParcelName(parcel.name)}
          />
        )}
      </Flex>

      <Flex align="center" overflow="hidden" style={{ minWidth: 0 }}>
        {loading ? (
          <SkeletonText length={10} />
        ) : (
          <RedoTableTextCell
            text={
              parcel.carrierParcel
                ? (getDimensionString(parcel.carrierParcel) ?? "-")
                : `${[parcel.length, parcel.width, parcel.height].join(" x ")} ${parcel.lengthUnit}.`
            }
          />
        )}
      </Flex>

      <Flex align="center" overflow="hidden" style={{ minWidth: 0 }}>
        {loading ? (
          <SkeletonText length={10} />
        ) : (
          <RedoTableTextCell
            text={
              parcel.carrierParcel
                ? "-"
                : `${parcel.weight} ${parcel.weightUnit}.`
            }
          />
        )}
      </Flex>

      <Flex align="center">
        {loading ? (
          <SkeletonText length={10} />
        ) : (
          <RedoTableCommandMenuCell items={menuItems} />
        )}
      </Flex>

      <Flex align="center" justify="center" px="xs">
        <SortableItemHandle asChild>
          <RedoButton
            aria-label="Drag to reorder"
            hierarchy="tertiary"
            IconLeading={DotsReorderIcon}
            size="xs"
            style={{ cursor: "grab" }}
            theme="normal"
          />
        </SortableItemHandle>
      </Flex>
    </SortableItem>
  );
});

// Sortable parcels table component
const SortableParcelsTable = memo(function SortableParcelsTable({
  parcels,
  onReorder,
  onEdit,
  onDelete,
  onSetDefault,
  onAddPackage,
  onManageWithAi,
  loading,
}: {
  parcels: ParcelValue[];
  onReorder: (parcels: ParcelValue[]) => void;
  onEdit: (index: number) => void;
  onDelete: (parcel: ParcelValue) => void;
  onSetDefault: (parcel: ParcelValue) => void;
  onAddPackage: () => void;
  onManageWithAi?: () => void;
  loading?: boolean;
}) {
  return (
    <CommandMenuProvider>
      <Flex
        borderColor="primary"
        borderRadius="lg"
        borderStyle="solid"
        borderWidth="1px"
        dir="column"
        gap="none"
        style={{ overflow: "hidden" }}
        w="full"
      >
        {/* Header */}
        <Flex dir="column" gap="xs" p="3xl" w="full">
          <Flex justify="space-between" w="full">
            <Text fontSize="md" fontWeight="semibold">
              Packages
            </Text>
            <Flex gap="md">
              {onManageWithAi && (
                <RedoButton
                  hierarchy="secondary"
                  onClick={onManageWithAi}
                  size="sm"
                  text="Manage with AI"
                />
              )}
              <RedoButton
                dataTestId="add-package-button"
                hierarchy="secondary"
                IconLeading={Plus}
                onClick={onAddPackage}
                size="sm"
                text="Add a package"
              />
            </Flex>
          </Flex>
        </Flex>

        {/* Table header row */}
        <Flex
          gap="none"
          style={{
            display: "grid",
            gridTemplateColumns: "3fr 2fr 1fr 80px 40px",
            height: "36px",
            backgroundColor: "var(--redo-colors-background-bg-secondary)",
            borderTop: "1px solid var(--redo-colors-border-border-secondary)",
            borderBottom:
              "1px solid var(--redo-colors-border-border-secondary)",
          }}
          w="full"
        >
          {["Name", "Size", "Weight"].map((label) => (
            <Flex align="center" grow="1" key={label} overflow="hidden" p="xl">
              <Text fontSize="xxs" fontWeight="medium" textColor="secondary">
                {label}
              </Text>
            </Flex>
          ))}
          <Flex />
          <Flex />
        </Flex>

        {/* Sortable rows */}
        <Flex dir="column" style={{ overflowY: "auto" }}>
          <Sortable
            getItemValue={parcelRowId}
            onValueChange={onReorder}
            orientation="vertical"
            value={parcels}
          >
            <SortableContent withoutSlot>
              {parcels.map((parcel, index) => (
                <SortableParcelRow
                  index={index}
                  key={parcelRowId(parcel)}
                  loading={loading}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onSetDefault={onSetDefault}
                  parcel={parcel}
                />
              ))}
            </SortableContent>
          </Sortable>
        </Flex>
      </Flex>
    </CommandMenuProvider>
  );
});

export const ParcelsPage = memo(function ParcelsPage() {
  const outboundLabelRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const [value, setValue] = useState<ParcelValue[] | undefined>(undefined);
  const reloadParcels = useContext(ReloadParcelsContext);
  const aiSlidePanel = useAiSlidePanel();

  useAskRedoPage({
    pageContext: PARCELS_PAGE_CONTEXT,
    reloadOn: PARCEL_CHANGES,
    reload: () => void reloadParcels?.(),
  });

  const openAiPanel = useHandler(() => {
    aiSlidePanel.open({});
  });

  const [parcelsLoad] = useLazyContext(ParcelContext);

  const carrierParcelsLoad = useLoad(
    async (signal) => {
      const { carrierParcels } = await outboundLabelRpcClient.getCarrierParcels(
        null,
        { signal },
      );
      return carrierParcels;
    },
    [outboundLabelRpcClient],
  );

  // Create parcels (batch)
  // Note: No invalidateParcels() - the response carries the persisted ids
  const [, doCreateParcels] = useTriggerLoadWithInput(
    async (
      parcelsToCreate: { parcel: ParcelValue; position: number }[],
      signal,
    ) => {
      const { parcels: persisted } = await outboundLabelRpcClient.createParcels(
        {
          parcels: parcelsToCreate.map(({ parcel, position }) =>
            parcelValueToParcel(parcel, position),
          ),
        },
        { signal },
      );

      const persistedIdBySubmittedId = new Map<string, string | undefined>(
        parcelsToCreate.map(({ parcel }, index) => [
          parcelRowId(parcel),
          persisted[index]?._id?.toString(),
        ]),
      );
      setValue((previous) =>
        previous
          ? adoptPersistedParcelIds(previous, persistedIdBySubmittedId)
          : previous,
      );
    },
  );

  // Update an existing parcel
  // Note: No invalidateParcels() - we use optimistic updates to avoid flicker
  const [, doUpdateParcel] = useTriggerLoadWithInput(
    async (
      {
        originalName,
        parcel,
        position,
      }: { originalName: string; parcel: ParcelValue; position: number },
      signal,
    ) => {
      await outboundLabelRpcClient.updateParcel(
        { originalName, parcel: parcelValueToParcel(parcel, position) },
        { signal },
      );
    },
  );

  // Reorder parcels
  // Note: No invalidateParcels() - we use optimistic updates to avoid flicker
  const [, doReorder] = useTriggerLoadWithInput(
    async (parcels: { name: string; position: number }[], signal) => {
      await outboundLabelRpcClient.reorderParcels({ parcels }, { signal });
    },
  );

  // Delete a parcel
  // Note: No invalidateParcels() - we use optimistic updates to avoid flicker
  const [, doDelete] = useTriggerLoadWithInput(async (name: string, signal) => {
    await outboundLabelRpcClient.deleteParcel({ name }, { signal });
  });

  const [parcelPendingDelete, setParcelPendingDelete] = useState<{
    parcel: ParcelValue;
    references: ParcelAutomationReference[];
  } | null>(null);

  const cancelDeleteParcel = useHandler(() => {
    setParcelPendingDelete(null);
  });

  const deleteParcel = useHandler(async (parcel: ParcelValue) => {
    if (!value) {
      return;
    }

    // Optimistically update local state
    const filteredParcels = value.filter((p) => p.name !== parcel.name);
    const reassignDefault = parcel.isDefault && filteredParcels.length > 0;

    if (reassignDefault) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      filteredParcels[0].isDefault = true;
      // Update the new default parcel on the server
      doUpdateParcel({
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        originalName: filteredParcels[0].name,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        parcel: filteredParcels[0],
        position: 0,
      });
    }

    setValue(filteredParcels);
    doDelete(parcel.name);
  });

  // Automation steps reference parcels by id, so deleting one silently breaks
  // every step pointing at it. Only interrupt when there is something to say.
  const [, requestDeleteParcel] = useTriggerLoadWithInput(
    async (parcel: ParcelValue, signal) => {
      const parcelId = parcelsLoad.value
        ?.find((p) => p.name === parcel.name)
        ?._id?.toString();
      if (!parcelId) {
        await deleteParcel(parcel);
        return;
      }
      const { references } =
        await outboundLabelRpcClient.getParcelAutomationReferences(
          { parcelId },
          { signal },
        );
      if (references.length === 0) {
        await deleteParcel(parcel);
        return;
      }
      setParcelPendingDelete({ parcel, references });
    },
  );

  const confirmDeleteParcel = useHandler(async () => {
    if (!parcelPendingDelete) {
      return;
    }
    await deleteParcel(parcelPendingDelete.parcel);
    setParcelPendingDelete(null);
  });

  const setDefaultParcel = useHandler(async (parcel: ParcelValue) => {
    if (!value) {
      return;
    }
    // Update local state optimistically
    const updatedParcels = value.map((p) => ({
      ...p,
      isDefault: p.name === parcel.name,
    }));
    setValue(updatedParcels);

    // Update on server - backend will automatically unset default on other parcels
    const parcelIndex = value.findIndex((p) => p.name === parcel.name);
    doUpdateParcel({
      originalName: parcel.name,
      parcel: { ...parcel, isDefault: true },
      position: parcelIndex,
    });
  });

  const handleReorder = useHandler((newOrder: ParcelValue[]) => {
    // Optimistically update local state
    setValue(newOrder);

    // Send reorder request with new positions
    const parcelsWithPositions = newOrder.map((p, index) => ({
      id: p.id,
      name: p.name,
      position: index,
    }));
    doReorder(parcelsWithPositions);
  });

  useEffect(() => {
    if (!parcelsLoad.value) {
      return;
    }
    setValue(parcelsLoad.value.map(parcelToParcelValue));
  }, [parcelsLoad]);

  const [modalOpen, setModalOpen] = useState(false);
  // Track which parcel is being edited (index) or null if creating new
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // Form input for editing
  const editInput = useInput(parcelInput, parcelDefault());
  // Form input for creating new parcels
  const createInput = useInput(parcelInput, parcelDefault());

  const openEditModal = useHandler((index: number) => {
    if (!value) {
      return;
    }
    const parcelToEdit = value[index];
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    editInput.setValue(parcelToEdit);
    setEditingIndex(index);
    setModalOpen(true);
  });

  const openCreateModal = useHandler(() => {
    // Initialize create form with appropriate defaults
    createInput.setValue(
      value?.length ? parcelDefault() : firstParcelDefault(),
    );
    setEditingIndex(null);
    setModalOpen(true);
  });

  const handleModalClose = useHandler((open: boolean) => {
    if (!open) {
      setEditingIndex(null);
    }
    setModalOpen(open);
  });

  const handleSave = useHandler(async (parcels: ParcelValue[]) => {
    if (!value) {
      return;
    }

    // Check if any of the new parcels is set as default
    const setNewDefault = parcels.some((p) => p.isDefault);

    if (editingIndex !== null && parcels.length === 1) {
      // Editing existing parcel - update in place
      const parcel = parcels[0];
      const originalParcel = value[editingIndex];
      if (!parcel || !originalParcel) {
        return;
      }
      const updatedParcels = value.map((existingParcel, index) => {
        if (index === editingIndex) {
          return parcel;
        }
        return {
          ...existingParcel,
          isDefault: setNewDefault ? false : existingParcel.isDefault,
        };
      });

      // Optimistically update local state
      setValue(updatedParcels);

      // Update the parcel on the server - backend will handle unsetting other defaults
      doUpdateParcel({
        originalName: originalParcel.name,
        parcel,
        position: editingIndex,
      });
    } else {
      // Creating new parcel(s) - append
      const startPosition = value.length;
      // Stand-in ids keep the new rows identifiable until the save returns
      const createdParcels = parcels.map((parcel) => ({
        ...parcel,
        id: parcel.id ?? crypto.randomUUID(),
      }));
      const updatedParcels = [
        ...value.map((existingParcel) => ({
          ...existingParcel,
          isDefault: setNewDefault ? false : existingParcel.isDefault,
        })),
        ...createdParcels,
      ];

      // Optimistically update local state
      setValue(updatedParcels);

      // Create the parcels on the server in batch - backend will handle unsetting other defaults
      doCreateParcels(
        createdParcels.map((parcel, i) => ({
          parcel,
          position: startPosition + i,
        })),
      );
    }
    setEditingIndex(null);
    setModalOpen(false);
  });

  if (value && value.length === 0) {
    return (
      <>
        <RedoEmptyState
          Icon={PackageIcon}
          primaryButtonProps={{
            dataTestId: "add-package-button",
            onClick: openCreateModal,
            text: "+ Add your first package",
          }}
          secondaryButtonProps={{
            onClick: openAiPanel,
            text: "Manage with AI",
          }}
          subtext="Add packages here and they will show up in order details to allow you to create and print labels."
          text="Add your first package."
          useNonArbiterPadding
        />
        <ParcelModal
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          carrierParcels={carrierParcelsLoad.value as CarrierParcel[]}
          existingParcels={value}
          input={createInput}
          numParcels={value.length}
          onSave={handleSave}
          open={modalOpen}
          setOpen={handleModalClose}
        />
      </>
    );
  }

  return (
    <Flex justify="center" w="full">
      <Flex dir="column" gap="5xl" pb="5xl" w="lg">
        <SortableParcelsTable
          loading={!value}
          onAddPackage={openCreateModal}
          onDelete={requestDeleteParcel}
          onEdit={openEditModal}
          onManageWithAi={openAiPanel}
          onReorder={handleReorder}
          onSetDefault={setDefaultParcel}
          parcels={
            value ||
            new Array(3)
              .fill(parcelDefault())
              .map((parcel, index) => ({
                ...parcel,
                id: `placeholder-${index + 1}`,
                name: `Parcel ${index + 1}`,
              }))
          }
        />
        <ParcelModal
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          carrierParcels={carrierParcelsLoad.value as CarrierParcel[]}
          existingParcels={value || []}
          input={editingIndex !== null ? editInput : createInput}
          isEditing={editingIndex !== null}
          numParcels={value?.length || 0}
          onSave={handleSave}
          open={modalOpen}
          setOpen={handleModalClose}
        />
        {parcelPendingDelete && (
          <DeleteParcelModal
            onCancel={cancelDeleteParcel}
            onConfirm={confirmDeleteParcel}
            parcelName={parcelPendingDelete.parcel.name}
            references={parcelPendingDelete.references}
          />
        )}
      </Flex>
    </Flex>
  );
});
